import { computed, effect, inject } from '@angular/core';
import { signalStore, withComputed, withHooks, withMethods } from '@ngrx/signals';

import { PendingCheckContextStore } from './pending-check-context.store';
import { PendingCheckCommentStore } from './pending-check-comment.store';
import { PendingCheckStageStore } from './pending-check-stage.store';
import { CheckStatus, ReworkStatus } from '../../../../core/models/checks-model';
import { PendingCheckDocumentStore } from './pending-check-document.store';
import { ProjectMemberStore } from './projectmember.store';
import {CurrentUserService} from '../../../../services/current-user';

function isCommentVisibleForPhase(
  commentCheckStatus: CheckStatus,
  currentCheckStatus: CheckStatus,
  reworkStatus: ReworkStatus
): boolean {
  if (currentCheckStatus <= CheckStatus['Kontrolle 1-2']) {
    return commentCheckStatus <= CheckStatus['Kontrolle 1-2'];
  }
  if (currentCheckStatus === CheckStatus['Kontrolle 2'] && reworkStatus !== ReworkStatus.None) {
    return commentCheckStatus <= CheckStatus['Kontrolle 2'];
  }
  return commentCheckStatus === currentCheckStatus;
}

export const PendingCheckFacadeStore = signalStore(
  { providedIn: 'root' },

  withComputed((
    _,
    contextStore = inject(PendingCheckContextStore),
    docStore = inject(PendingCheckDocumentStore),
    commentStore = inject(PendingCheckCommentStore),
    stageStore = inject(PendingCheckStageStore),
    projectMemberStore = inject(ProjectMemberStore),
    currentUserService = inject(CurrentUserService),
  ) => ({

    isLoading: computed(() =>
      contextStore.isLoading() || docStore.isLoading() || commentStore.isLoading()
    ),
    currentCheck: contextStore.currentCheck,
    checkInfoViewModel: contextStore.checkInfoViewModel,
    documentsForCheck: docStore.documentsForCheck,
    currentUser: currentUserService.currentUser,
    projectCode: contextStore.projectCode,
    currentStages: stageStore.stages,

    // Pass-throughs für den ProjectMemberStore (Checker-Auswahllisten in check-info-card)
    users: projectMemberStore.users,
    usersForChecker12: projectMemberStore.usersForChecker12,
    usersForChecker12Consultant: projectMemberStore.usersForChecker12Consultant,
    usersForChecker2: projectMemberStore.usersForChecker2,

    generalComments: computed(() => {
      const allComments = commentStore.visibleComments();

      if (!docStore.documentCommentsLoaded()) return [];

      const docCommentGroups = docStore.documentComments();

      const docCommentGuids = new Set(
        docCommentGroups.flatMap(group => group.comments.map(c => c.id))
      );

      if (docStore.hasFailedDocumentComments()) {
        console.warn(
          '[Facade] Dokument-Kommentare unvollständig geladen – ' +
          'Zuordnung "allgemein vs. dokumentbezogen" ist nicht verlässlich.'
        );
      }

      return allComments.filter(c => !docCommentGuids.has(c.id));
    }),

    documentCommentsIncomplete: docStore.hasFailedDocumentComments,


    activeStageId: computed<number | null>(() => {
      const check = contextStore.currentCheck();
      const stages = stageStore.stages();
      if (!check || stages.length === 0) return null;

      let targetPhase: string;
      switch (check.status) {
        case CheckStatus['Kontrolle 1-1']: targetPhase = 'FIRST';  break;
        case CheckStatus['Kontrolle 1-2']: targetPhase = 'SECOND'; break;
        case CheckStatus['Kontrolle 2']:   targetPhase = 'REVIEW'; break;
        default: return null;
      }
      return stages.find(s => s.phase === targetPhase)?.id ?? null;
    }),

    documentTableViewModel: computed(() => {
      const currentCheck = contextStore.currentCheck();
      if (!currentCheck) return [];

      const generalComments = commentStore.comments();
      const statusByGuid = new Map(generalComments.map(c => [c.id, c.checkStatus]));
      const checkCommentGuids = new Set(generalComments.map(c => c.id));

      return docStore.documentComments().map(row => ({
        document: row.document,
        comments: row.comments
          .filter(c => checkCommentGuids.has(c.id))
          .map(c => ({ ...c, checkStatus: statusByGuid.get(c.id) ?? c.checkStatus }))
          .filter(c =>
            isCommentVisibleForPhase(c.checkStatus, currentCheck.status, currentCheck.reworkStatus)
          ),
      }));
    })
  })),

  withMethods((
    store,
    contextStore = inject(PendingCheckContextStore),
    docStore = inject(PendingCheckDocumentStore),
    commentStore = inject(PendingCheckCommentStore),
    stageStore = inject(PendingCheckStageStore),
    projectMemberStore = inject(ProjectMemberStore)
  ) => ({

    initializeCheck(checkId: string | null, projectCode: string | null) {
      contextStore.setCheckContext(checkId, projectCode);

      if (checkId) {
        stageStore.loadStagesByCheckId(checkId);
        docStore.loadDocumentsByCheckId(checkId);
        commentStore.loadCommentsByCheckId(checkId);
      }
      // Projektmitglieder hängen an projectCode, nicht an checkId – auch dann
      // laden, wenn checkId (noch) null ist, solange ein Projekt bekannt ist.
      projectMemberStore.loadMembersByProjectCode(projectCode);
    },

    async addGeneralComment(text: string, severity: string = 'INFO') {
      const checkId = contextStore.checkId();
      const stageId = store.activeStageId();

      if (checkId && stageId != null) {
        await commentStore.addComment(checkId, stageId, text, severity);
      } else {
        console.warn('Kommentar konnte nicht gespeichert werden: checkId oder stageId fehlt.');
      }
    },

    async addDocumentComment(documentId: string, text: string, severity: string) {
      const checkId = contextStore.checkId();
      const stageId = store.activeStageId();

      if (checkId && stageId != null) {
        await commentStore.addDocumentComment(checkId, stageId, documentId, text, severity);
        docStore.loadCommentsForDocuments(docStore.documentsForCheck());
      }
    },

    async updateCommentStatus(guid: string, isDone: boolean) {
      const checkId = contextStore.checkId();
      if (checkId) {
        await commentStore.updateCommentStatus(checkId, guid, isDone);
        docStore.loadCommentsForDocuments(docStore.documentsForCheck());
      }
    },

    async setCommentSeverity(guid: string, severity: string) {
      const checkId = contextStore.checkId();
      if (checkId) {
        await commentStore.updateCommentSeverity(checkId, guid, severity);
        docStore.loadCommentsForDocuments(docStore.documentsForCheck());
      }
    },

    async deleteComment(guid: string) {
      const checkId = contextStore.checkId();
      if (checkId) {
        await commentStore.deleteComment(checkId, guid);
        docStore.loadCommentsForDocuments(docStore.documentsForCheck());
      }
    },

    /** Erzwingt einen Reload des aktuellen Checks (z. B. nach Stage-/Status-Updates). */
    refreshCheck() {
      contextStore.refreshCheck();
    },

    patchReworkStatusLocally(reworkStatus: ReworkStatus) {
      contextStore.patchReworkStatusLocally(reworkStatus);
    },

    /** Erzwingt einen Reload der Dokumentliste des aktuellen Checks. */
    refreshDocuments() {
      const checkId = contextStore.checkId();
      docStore.refreshDocuments(checkId);
    }

  })),

  // Orchestrierung des zweiten Ladeschritts: sobald sich die Dokumentliste
  // ändert (nach initializeCheck oder refreshDocuments), lädt die Facade
  // gezielt die Kommentare pro Dokument nach.
  withHooks({
    onInit(store) {
      const docStore = inject(PendingCheckDocumentStore);
      effect(() => {
        const docs = docStore.documentsForCheck();
        docStore.loadCommentsForDocuments(docs);
      });
    }
  })
);
